import { definitionNotHeld, handBack, itemField, record, scalar, upper, type Row } from '../ability/context.js';
import { layerListEntryUuid } from '../actions/spell-components.js';
import { plCatalogs } from '../pl/contract.js';
import { heldLayerlessDefinition } from '../compile/held-definition.js';
import { statusPaused } from '../statuses/expiry.js';
import { ftSanguineOverwhelmingSpellIsReplaySafe } from '../port/k3-rules/sanguine_cast_overwhelming.js';
import { geometryEnvironment } from '../geometry/port-environment.js';
import type { NextSpellCarrierDeps } from '../port/k3-rules/next_spell_numeric_carriers.js';
import { instantProjectileCount } from '../../shared/projectile-targeting.js';

import { NEXT_SPELL_LAYERS } from './next-spell-layers.js';
export { NEXT_SPELL_LAYERS } from './next-spell-layers.js';
export type NextSpellLayer = typeof NEXT_SPELL_LAYERS[number];
export interface NextSpellReaders {
  value(row: Row | null, key: string): unknown;
  has(row: Row | null, key: string): boolean;
  entries(row: Row | null, key: string): unknown[];
  statuses(row: Row): Row[];
  definition?(id: string): Row | null;
  resolveItem?(entry: unknown): Row | null;
}
export interface NextSpellCarrier { status: Row; definition: Row; layers: NextSpellLayer[] }
export interface NextSpellPlan {
  carriers: NextSpellCarrier[];
  ap: number | null;
  boost: number;
  damagePercent: number;
  /** Widened's summed percentage: a volley's projectiles read it (prepareNextSpellProjectile) since the parent carries no area of its own. */
  areaPercent: number;
  /** Careful applied: the parent's own area is stamped target_opposing_team_only; a volley's projectiles take the same stamp from here. */
  opposingOnly: boolean;
  /** A VOLLEY PARENT's plan (judged against its projectile): the per-recipient payloads (Fearsome's rider, Umbral's zone, Encouraging's
   *  offer) are delivered once after every projectile, on the creatures the projectiles reached (ability-use.ts → dispatchNextSpellPayloads),
   *  and Overwhelming replays each projectile's dispatch — never the container's own dispatch, whose only target is the caster. */
  volleyParent: boolean;
  duplicate: boolean;
  twin: boolean;
  bounce: boolean;
  damageType: string | null;
  targets: string[];
}
const SELF = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
const REACH = '36D59FF4-6778-96ED-8664-F5D63638C77B';
const SLOW = new Set(['one_minute', 'ten_minutes', 'one_hour', 'eight_hours', 'twelve_hours', 'twenty_four_hours']);
const NOT_CAST = new Set(['spell_ritual', 'finish_casting', 'finish_casting_ritual_spell', 'damage_only', 'moving_into_p_aoe', 'end_turn_in_p_aoe', 'dot_status_tick']);
const filled = (v: unknown): boolean => v != null && v !== '' && v !== 0 && v !== '0' && v !== false;
const number = (read: Pick<NextSpellReaders, 'value'>, row: Row, key: string): number => {
  const value = Number(scalar(read.value(row, key)));
  return Number.isFinite(value) ? Math.trunc(value) : 0;
};
export const nextSpellPlan = (ability: Row): NextSpellPlan | null =>
  record(record(ability.computed)?.next_spell) as unknown as NextSpellPlan | null;

export function assertNextSpellPayload(read: Pick<NextSpellReaders, 'has'>, row: Row): void {
  for (const key of Object.keys(record(row.item_layer_types) ?? record(row.layers) ?? {}))
    if (key.startsWith('next_spell_') && key !== 'next_spell_mana_discount' && !NEXT_SPELL_LAYERS.includes(key as NextSpellLayer)
        && read.has(row, key)) handBack(`next-spell-unported:${key}`);
}

/** Resolve lean status shells through the page catalog, never through a hand-made flattened layer view. */
export function nextSpellCarriers(read: Omit<NextSpellReaders, 'entries'>, caster: Row): NextSpellCarrier[] {
  const carriers: NextSpellCarrier[] = [];
  for (const status of read.statuses(caster)) {
    if (Number(scalar(status.status_deleted) ?? 0) !== 0 || statusPaused(status)) continue;
    const id = upper(layerListEntryUuid(status.status_item_id ?? itemField(record(status.status_arr), 'item_id')));
    const definition = [id ? read.definition?.(id) : null, id ? read.resolveItem?.(id) : null, record(status.status_arr)]
      .find(row => row && (record(row.item_layer_types) || record(row.layers) || heldLayerlessDefinition(row)));
    if (!definition) definitionNotHeld('template_spell_roll:status-definition-not-held', id);
    assertNextSpellPayload(read, definition);
    const layers = NEXT_SPELL_LAYERS.filter(key => read.has(definition, key)
      && (key === 'next_spell_ap_override' || filled(scalar(read.value(definition, key)))));
    if (layers.length) carriers.push({ status, definition, layers });
  }
  return carriers;
}

export function fastNextSpell(read: Pick<NextSpellReaders, 'value'>, ability: Row, variable?: unknown): boolean {
  return ['spell', 'curse'].includes(String(itemField(ability, 'item_type')))
    && !NOT_CAST.has(String(variable ?? '')) && !SLOW.has(String(read.value(ability, 'spell_casting_time') ?? ''));
}
/** A volley parent's Self range is the projectile container's marker (instantProjectileCount), not a Self target Imparted could move. */
const VOLLEY_PARENT_LAYER = 'spell_upcast_additional_projectiles_created';
export function nextSpellRangeId(read: Omit<NextSpellReaders, 'entries'>, caster: Row, ability: Row): string {
  const range = upper(read.value(ability, 'range'));
  return range === SELF && fastNextSpell(read, ability)
    && !['aoe_type', 'persistent_aoe', 'aoe_follows_target', VOLLEY_PARENT_LAYER].some(key => read.has(ability, key))
    && nextSpellCarriers(read, caster).some(c => c.layers.includes('next_spell_self_range_becomes_reach')) ? REACH : range;
}
export function nextSpellNumericTotal(read: Omit<NextSpellReaders, 'entries'>, caster: Row, key: NextSpellLayer, cap: number): number {
  return Math.min(cap, nextSpellCarriers(read, caster).filter(c => c.layers.includes(key))
    .reduce((total, c) => total + Math.max(0, number(read, c.definition, key)), 0));
}
function boostEligible(read: Pick<NextSpellReaders, 'value' | 'has'>, ability: Row): boolean {
  return Number(read.value(ability, 'spell_level')) > 0 && read.has(ability, 'spell_upcastable')
    && !['consumes_charges_qty', 'change_caster_mana'].some(k => read.value(ability, k) === '[x]');
}
export function nextSpellBoostLevels(read: Omit<NextSpellReaders, 'entries'>, caster: Row, ability: Row, variable?: unknown): number {
  return fastNextSpell(read, ability, variable) && boostEligible(read, ability)
    ? nextSpellNumericTotal(read, caster, 'next_spell_upcast_levels', 3) : 0;
}
/** Whether the caster holds an ARMED next-spell carrier, judged from the definitions the page HOLDS — an unheld status is skipped, never a
 *  hand-back. The sheet's pre-check (action-bar.ts armInstantProjectileSpell): a caster with no armed carrier takes the plain pool exactly as
 *  before and nothing is judged; only an armed one runs nextSpellCarriers, which hands back an unheld definition as the resolver does. */
export function nextSpellCarrierHeld(read: Omit<NextSpellReaders, 'entries'>, caster: Row): boolean {
  for (const status of read.statuses(caster)) {
    if (Number(scalar(status.status_deleted) ?? 0) !== 0 || statusPaused(status)) continue;
    const id = upper(layerListEntryUuid(status.status_item_id ?? itemField(record(status.status_arr), 'item_id')));
    const definition = [id ? read.definition?.(id) : null, id ? read.resolveItem?.(id) : null, record(status.status_arr)]
      .find(row => row && (record(row.item_layer_types) || record(row.layers) || heldLayerlessDefinition(row)));
    if (definition && NEXT_SPELL_LAYERS.some(key => read.has(definition, key)
      && (key === 'next_spell_ap_override' || filled(scalar(read.value(definition, key)))))) return true;
  }
  return false;
}
/** The definition a `…-not-held:<ID>` hand-back names (context.ts definitionNotHeld), for a caller that hydrates it and asks again. */
export function notHeldDefinitionId(reason: string): string | undefined {
  return /-not-held:([0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12})$/i.exec(reason)?.[1]?.toUpperCase();
}
/** The instant projectile pool a volley parent OFFERS (the sheet) — the engine sizes it the same way from the cast's plan (ability-use.ts,
 *  the volley block): Boost's levels grow it exactly as a level pick does. No armed carrier (nextSpellCarrierHeld) → the plain pool, nothing
 *  judged; a marker cast (`spellType`) is never boosted (fastNextSpell). Throws the resolver's
 *  `template_spell_roll:status-definition-not-held:<ID>` when an armed caster's status definition is not held. */
export function nextSpellVolleyPool(read: Omit<NextSpellReaders, 'entries'>, caster: Row, parent: Row, childCharges: unknown,
  castLevel?: string, spellType = ''): { count: number | null; boost: number } {
  const type = itemField(parent, 'item_type'), layer = (key: string): unknown => read.value(parent, key);
  const plain = instantProjectileCount(type, layer, childCharges, castLevel);
  if (plain === null || !nextSpellCarrierHeld(read, caster)) return { count: plain, boost: 0 };
  const boost = nextSpellBoostLevels(read, caster, parent, spellType || castLevel);
  if (boost <= 0) return { count: plain, boost: 0 };
  const chosen = /^\d{1,2}$/.test(String(castLevel ?? '').trim()) ? Number(castLevel) : 0;
  return { count: instantProjectileCount(type, layer, childCharges, Math.max(number(read, parent, 'spell_level'), chosen) + boost), boost };
}
export function widenNextSpellDimension(base: number, percent: number): number {
  return base > 0 && percent > 0 ? Math.max(base, Math.floor(base * (100 + percent) / 500) * 5) : base;
}
/** Twin and Bounce share their single-target contract; Bounce additionally requires an attack. */
export function nextSpellSingleTarget(read: Pick<NextSpellReaders, 'value' | 'has'>, ability: Row, variable?: unknown, bounce = false): boolean {
  if (!fastNextSpell(read, ability, variable)) return false;
  const range = upper(read.value(ability, 'range'));
  if (!range || range === SELF || ['aoe_type', 'persistent_aoe', 'aoe_follows_target', 'spell_upcast_additional_projectiles_created', 'summon']
    .some(key => read.has(ability, key))) return false;
  const max = read.value(ability, 'max_targets');
  if (max != null && (!Number.isFinite(Number(scalar(max))) || Number(scalar(max)) >= 2)) return false;
  return !bounce || ['5740CA49-006E-A7A0-B46B-6DDED6CD546C', 'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C'].includes(upper(read.value(ability, 'roll_type')));
}

/** Cast-local data only. The returned copy is also used by admission and placed-area expansion.
 *  `projectile` is a volley's child (the parent's single free_ability entry, never a spell itself): the parent is the spell the carriers
 *  modify, so its plan is judged against the projectile's area / damage layers too — the parent consumes the carriers once at its own
 *  settlement, every projectile is rewritten from that plan by prepareNextSpellProjectile before its expansion and dispatch, and the
 *  per-recipient payloads reach the creatures the projectiles hit (plan.volleyParent → ability-use.ts). A volley parent cast WITHOUT its
 *  projectile (no volley frame) has nothing to judge or reach: no carrier applies and every one stays armed. */
export function prepareNextSpell(read: NextSpellReaders, caster: Row, ability: Row, variable1?: unknown, variable2?: unknown,
  projectile?: Row | null): Row {
  if (nextSpellPlan(ability) || !fastNextSpell(read, ability, variable1)) return ability;
  const held = nextSpellCarriers(read, caster);
  if (!held.length) return ability;
  const plan: NextSpellPlan = { carriers: [], ap: null, boost: 0, damagePercent: 0, areaPercent: 0, opposingOnly: false, volleyParent: false,
    duplicate: false, twin: false, bounce: false, damageType: null, targets: [] };
  const computed: Row = { ...record(ability.computed), next_spell: plan };
  const layers: Row = { ...record(ability.item_layer_types) };
  const areaPercent = nextSpellNumericTotal(read, caster, 'next_spell_aoe_changed_by_percentage', 300);
  plan.boost = nextSpellBoostLevels(read, caster, ability, variable1);
  plan.damagePercent = nextSpellNumericTotal(read, caster, 'next_spell_damage_dice_changed_by_percentage', 300);
  const area = read.has(ability, 'aoe_type');
  // A volley parent has no area, damage or recipients of its own (its Self range is the projectile container's marker): the projectile's
  // layers decide whether Widened / Careful / Reverberation / Elemental / Overwhelming reach the spell, the projectiles' hits are the
  // recipients of Fearsome / Umbral / Encouraging, and Boost grows the projectile pool. Twin / Bounce (a single-target spell), Imparted (a
  // Self-targeted spell) and Distant (the spell's own range) are not covered by a volley: they stay armed for the next spell they do cover.
  const volley = read.has(ability, VOLLEY_PARENT_LAYER);
  const projectiles = volley && !!projectile;
  plan.volleyParent = projectiles;
  const projectileArea = projectiles && read.has(projectile ?? null, 'aoe_type');
  const projectileDamage = projectiles && read.has(projectile ?? null, 'damage_type');
  for (const carrier of held) {
    const applied: NextSpellLayer[] = [];
    for (const key of carrier.layers) {
      const value = number(read, carrier.definition, key);
      let applies = true;
      switch (key) {
        case 'next_spell_ap_override':
          applies = value >= 0 && /^\d+ap$/.test(String(read.value(ability, 'spell_casting_time') ?? '3ap'));
          if (applies) plan.ap = Math.min(plan.ap ?? value, value);
          break;
        case 'next_spell_upcast_levels':
          applies = boostEligible(read, ability) && (!volley || projectiles);
          if (!applies) plan.boost = 0;
          break;
        case 'next_spell_damage_dice_changed_by_percentage':
          applies = read.has(ability, 'damage_type') || projectileDamage;
          break;
        case 'next_spell_aoe_changed_by_percentage':
          applies = area || projectileArea;
          if (applies) plan.areaPercent = areaPercent;
          break;
        case 'next_spell_range_multiplier': applies = upper(read.value(ability, 'range')) !== SELF; break;
        case 'next_spell_self_range_becomes_reach':
          applies = upper(read.value(ability, 'range')) === SELF && nextSpellRangeId(read, caster, ability) === REACH;
          if (applies) layers.range = REACH;
          break;
        case 'next_spell_concentration_uncapped':
          applies = read.has(ability, 'spell_concentration');
          if (applies) computed.concentration_duration_uncapped = 1;
          break;
        case 'next_spell_hits_opposing_team_only':
          applies = area || projectileArea;
          if (area) layers.target_opposing_team_only = 1;
          if (applies) plan.opposingOnly = true;
          break;
        case 'next_spell_twin_cast': applies = nextSpellSingleTarget(read, ability, variable1); plan.twin ||= applies; break;
        case 'next_spell_bounce_cast': applies = nextSpellSingleTarget(read, ability, variable1, true); plan.bounce ||= applies; break;
        case 'next_spell_aoe_effect_duplicated': {
          applies = area || projectileArea;
          if (!applies) break;
          // The replayed dispatch is the one with the area: a volley's PROJECTILE (its template runs dispatchNextSpell on the plan copy
          // prepareNextSpellProjectile hands it), never the parent container — so the replay contract is judged on the projectile.
          const host: Row = projectiles && projectile ? projectile : ability;
          if (read.has(host, 'persistent_aoe')) handBack(`next-spell-unported:${key}:persistent_aoe`);
          const safe = ftSanguineOverwhelmingSpellIsReplaySafe(host, geometryEnvironment<Pick<NextSpellCarrierDeps, 'layer_has'>>({
            layer_has: (row, key) => read.has(row as Row, key),
          }));
          if (!safe) handBack(`next-spell-unported:${key}:replay-contract`);
          plan.duplicate = true;
          break;
        }
        case 'next_spell_damage_type_choice': {
          applies = read.has(ability, 'damage_type') || projectileDamage;
          const pick = upper(scalar(variable2) ?? scalar(variable1));
          if (applies) {
            const allowed = read.entries(carrier.definition, 'damage_type').map(e => upper(layerListEntryUuid(e)));
            if (!/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(pick)) handBack(`next-spell-unported:${key}:choose-damage-type`);
            // Multiple choice carriers offer their union; a Transmuted carrier permits the whole catalog.
            const choiceCarriers = held.filter(c => c.layers.includes(key));
            const permits = !allowed.length || allowed.includes(pick) || choiceCarriers.some(c => {
              const ids = read.entries(c.definition, 'damage_type').map(e => upper(layerListEntryUuid(e)));
              return !ids.length || ids.includes(pick);
            });
            if (!permits) handBack(`next-spell-unported:${key}:invalid-damage-type`);
            const definition = read.definition?.(pick) ?? read.resolveItem?.(pick);
            if (!definition) definitionNotHeld('damage_type_roll:damage-type-definition-not-held', pick);
            if (!plCatalogs().damageTypes.getById(pick) && itemField(definition, 'item_type') !== 'damage_type')
              handBack(`next-spell-unported:${key}:invalid-damage-type`);
            plan.damageType = pick;
          }
          break;
        }
        // The per-recipient payloads: on a volley the recipients are the creatures its projectiles hit (each projectile's effect gate queues
        // them on its plan copy; ability-use.ts delivers the parent's payloads once to each after the last projectile).
        case 'next_spell_target_rider':
          applies = !volley || projectiles;
          if (applies && (!read.entries(carrier.definition, 'skill_roll_contestee').length
            || !read.entries(carrier.definition, 'status_effect_applied_to_target_after_contesting_roll_failed').length))
            handBack(`next-spell-unported:${key}:rider-definition`);
          break;
        case 'next_spell_paoe_applied_to_targets': {
          applies = !volley || projectiles;
          if (!applies) break;
          const id = upper(layerListEntryUuid(read.value(carrier.definition, key)));
          const zone = read.definition?.(id) ?? read.resolveItem?.(id);
          if (!zone || !record(zone.item_layer_types)) definitionNotHeld(`next-spell-unported:${key}:definition-not-held`, id);
          if (!read.has(zone, 'persistent_aoe') || !read.has(zone, 'aoe_follows_target') || !read.has(zone, 'aoe_type'))
            handBack(`next-spell-unported:${key}:zone-definition`);
          break;
        }
        case 'next_spell_target_opportunity_attack': applies = !volley || projectiles; break; // Offered to the recipient's controller after the spell commits.
      }
      if (applies) applied.push(key);
    }
    if (applied.length) plan.carriers.push({ ...carrier, layers: applied });
  }
  if (!plan.carriers.length) return ability;
  const baseLevel = number(read, ability, 'spell_level');
  const chosenLevel = /^\d+$/.test(String(variable1 ?? '')) ? Number(variable1) : baseLevel;
  const growth = Math.max(0, number(read, ability, 'spell_upcast_increase_aoe_ft'))
    * Math.max(0, Math.max(baseLevel, chosenLevel) + plan.boost - baseLevel);
  for (const key of ['aoe_distance', 'aoe_distance_height', 'aoe_distance_depth']) {
    const base = Number(read.value(ability, key)), extra = key === 'aoe_distance' ? growth : 0;
    if (area && base > 0 && (areaPercent > 0 || extra > 0)) computed[key] = widenNextSpellDimension(base + extra, areaPercent);
  }
  if (area && growth > 0) computed.next_spell_area_growth_applied = 1;
  return { ...ability, item_layer_types: layers, computed };
}

/** A volley's projectile cast from a prepared parent: the parent's plan shapes it the way a single placed spell is shaped before its
 *  expansion — Widened dimensions on computed.aoe_* (the expansion's per-cast bridge), Careful's target_opposing_team_only stamp, the
 *  plan's Reverberation dice / Elemental retype for its damage rolls, and Overwhelming's `duplicate` (its template replays the dispatch —
 *  dispatchNextSpell). The copy carries NO carriers: the parent's settlement consumed them once, and a projectile dispatch
 *  (templateAbilitiesRoll) never settles a plan. Its `targets` start empty and collect the creatures this projectile's effect gate passed
 *  (queueNextSpellTarget) — the volley's recipients for the parent's per-recipient payloads (ability-use.ts). */
export function prepareNextSpellProjectile(read: Pick<NextSpellReaders, 'value' | 'has'>, parent: Row, projectile: Row): Row {
  const plan = nextSpellPlan(parent);
  if (!plan || nextSpellPlan(projectile)) return projectile;
  const computed: Row = { ...record(projectile.computed),
    next_spell: { ...plan, carriers: [], targets: [], ap: null, boost: 0, twin: false, bounce: false, volleyParent: false } satisfies NextSpellPlan };
  const layers: Row = { ...record(projectile.item_layer_types) };
  if (read.has(projectile, 'aoe_type')) {
    if (plan.opposingOnly) layers.target_opposing_team_only = 1;
    for (const key of ['aoe_distance', 'aoe_distance_height', 'aoe_distance_depth']) {
      const base = Number(read.value(projectile, key));
      if (base > 0 && plan.areaPercent > 0) computed[key] = widenNextSpellDimension(base, plan.areaPercent);
    }
  }
  return { ...projectile, item_layer_types: layers, computed };
}

export function nextSpellDamageDice(ability: Row, dice: number): number {
  const percent = nextSpellPlan(ability)?.damagePercent ?? 0;
  return dice > 0 ? dice + Math.round(dice * percent / 100) : dice;
}
